import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ShoppingBag } from "lucide-react";

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [talents, setTalents] = useState([]);
  const [meta, setMeta] = useState({ platforms: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ talent_id: "", platform: "TikTok Shop", product_name: "", gmv: "" });

  const load = () => api.get("/sales").then((res) => setSales(res.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/talents").then((res) => setTalents(res.data.filter((t) => t.status === "active"))).catch(() => {});
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.talent_id || !form.product_name || !form.gmv) {
      toast.error("Lengkapi semua field");
      return;
    }
    try {
      await api.post("/sales", { ...form, gmv: Number(form.gmv) });
      toast.success("Penjualan afiliasi dicatat & komisi dihitung");
      setOpen(false);
      setForm({ talent_id: "", platform: "TikTok Shop", product_name: "", gmv: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sales-container">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Penjualan Afiliasi</h1>
          <p className="text-muted-foreground mt-1">Catat order & hitung komisi otomatis per talent</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-sale-button" className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="h-4 w-4 mr-2" /> Catat Penjualan
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-primary" /> Penjualan Baru</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Talent</Label>
                <Select value={form.talent_id} onValueChange={(v) => setForm({ ...form, talent_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="sale-talent-select"><SelectValue placeholder="Pilih talent" /></SelectTrigger>
                  <SelectContent>
                    {talents.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Platform</Label>
                <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.platforms.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nama Produk</Label>
                <Input data-testid="sale-product-input" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>GMV (Rp)</Label>
                <Input data-testid="sale-gmv-input" type="number" value={form.gmv} onChange={(e) => setForm({ ...form, gmv: e.target.value })} className="mt-1.5" placeholder="1000000" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="sale-save-button" onClick={submit} className="bg-primary hover:bg-primary/90 text-white w-full">Simpan & Hitung Komisi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sales-table">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="px-4 py-3 font-medium">Order</th>
                <th className="px-4 py-3 font-medium">Tanggal</th>
                <th className="px-4 py-3 font-medium">Talent</th>
                <th className="px-4 py-3 font-medium">Platform</th>
                <th className="px-4 py-3 font-medium">Produk</th>
                <th className="px-4 py-3 font-medium text-right">GMV</th>
                <th className="px-4 py-3 font-medium text-right">Komisi Talent</th>
                <th className="px-4 py-3 font-medium text-right">Revenue Agency</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((s) => (
                <tr key={s.id} className="border-b border-border/50 hover:bg-secondary/40 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{s.order_id}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(s.date)}</td>
                  <td className="px-4 py-3 font-medium">{s.talent_name}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded bg-secondary">{s.platform}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{s.product_name}</td>
                  <td className="px-4 py-3 text-right font-mono text-cyan-400">{rupiah(s.gmv)}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-400">{rupiah(s.talent_commission)}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-400">{rupiah(s.agency_revenue)}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                </tr>
              ))}
              {sales.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Belum ada penjualan.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
